<template>
  <div class="share-button" ref="button">
    <slot :onClick="onClick" :loading="loading"></slot>
  </div>
</template>

<script>

import shareEvents from '@/utils/shareEvents';
import SharePopup from '@/base-components/share/SharePopup';
import { mapActions } from 'vuex';
import Vue from 'vue';
import html2canvas from 'html2canvas';
import shareUtils from '@/utils/share';

export default {
  name: 'ShareButton',
  props: {
    shareComponent: {
      type: Object,
      required: true,
    },
    shareComponentProps: {
      type: Object,
    },
    shareName: {
      type: String,
      required: true,
    },
    filename: {
      type: String,
      required: true,
    },
    createImpressionOnClick: {
      type: Boolean,
      default: false,
    },
    dontSendImpression: {
      type: Boolean,
      default: false,
    },
    showPopup: {
      type: Boolean,
      default: true,
    },
    segmentExtraProps: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  created() {
    if (!this.createImpressionOnClick) {
      this.sendImpressionEvent();
    }
  },
  computed: {
    segmentData() {
      return {
        shareName: this.shareName,
        ...this.segmentExtraProps,
      };
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    async onClick() {
      if (this.createImpressionOnClick) {
        this.sendImpressionEvent();
      }
      if (this.showPopup) {
        this.sendShareIntentEvent();
        this.openModal({
          component: SharePopup,
          props: {
            shareComponent: this.shareComponent,
            shareComponentProps: this.shareComponentProps,
            filename: this.filename,
            segmentData: this.segmentData,
          },
        });
      } else {
        this.sendNativeShareClicked();
        await this.createShareAsset();
      }
    },
    sendImpressionEvent() {
      if (!this.dontSendImpression) {
        shareEvents.trackImpression(this.segmentData);
      }
    },
    sendShareIntentEvent() {
      shareEvents.trackShareIntentClicked(this.segmentData);
    },
    sendNativeShareClicked() {
      shareEvents.trackNativeShareClicked(this.segmentData);
    },
    async createShareAsset() {
      this.loading = true;
      try {
        const canvas = await this.createCanvas();
        await shareUtils.shareImage(canvas, `${this.filename}.png`, this.segmentData);
      } finally {
        this.loading = false;
      }
    },
    async createCanvas() {
      const MyClass = Vue.extend(this.shareComponent);
      const instance = new MyClass({
        propsData: this.shareComponentProps,
      });
      instance.$mount();
      instance.$el.style.position = 'fixed';
      instance.$el.style.visibility = 'hidden';
      // We're using the parent of the button in order to inherti the closest css properties to the current location we're in
      // but without getting the css properties of the button (text color for example)
      const parent = this.$refs.button.parentNode;
      parent.appendChild(instance.$el);

      try {
        // Seems like the html2canvas function takes a lot of CPU before it starts it async work, so if we're running it without the wait,
        // There is a render issue that seems stuck (the loader doesn't start the render)
        await waitFor(0);
        return await html2canvas(instance.$el, {
          onclone: (documentClone, clonedElement) => {
            // eslint-disable-next-line no-param-reassign
            clonedElement.style.visibility = 'visible';
          },
        });
      } finally {
        parent.removeChild(instance.$el);
      }
    },
  },
};

async function waitFor(ms = 0) {
  return new Promise(res => setTimeout(res, ms));
}
</script>

<style lang="scss" scoped>
</style>
